export interface MobileFooterBannerRouteDecision {
  eligible: boolean;
  reason: string;
}

const EXCLUDED_EXACT_ROUTES = new Set([
  '/',
  '/tutorial',
  '/mood',
  '/email-sent',
  '/check-version',
  '/deepLink',
]);

const EXCLUDED_PREFIXES = [
  '/auth',
  '/payments',
  '/admin',
  '/pending-approval',
  '/approvement',
];

const ELIGIBLE_PREFIXES = [
  '/media',
  '/bazaar',
  '/events',
  '/planet',
  '/merfolk',
  '/profile',
  '/chat',
  '/pool',
  '/pod',
];

const EXCLUDED_FORM_SEGMENTS = new Set([
  'add',
  'edit',
  'create',
  'settings',
  'notification_settings',
]);

const EXCLUDED_ROUTE_WORDS = [
  'premium',
  'package',
  'subscription',
  'subscription-by-pearls',
];

export function getMobileFooterBannerRouteDecision(
  rawUrl: string
): MobileFooterBannerRouteDecision {
  const path = normalizePath(rawUrl);
  const lowerPath = path.toLowerCase();
  const segments = lowerPath.split('/').filter(Boolean);

  if (EXCLUDED_EXACT_ROUTES.has(path)) {
    return { eligible: false, reason: 'excluded-exact-route' };
  }

  if (isPoolDetailRoute(lowerPath)) {
    return { eligible: false, reason: 'excluded-pool-detail-route' };
  }

  if (EXCLUDED_PREFIXES.some(prefix => lowerPath === prefix || lowerPath.startsWith(`${prefix}/`))) {
    return { eligible: false, reason: 'excluded-prefix' };
  }

  if (EXCLUDED_ROUTE_WORDS.some(word => segments.includes(word))) {
    return { eligible: false, reason: 'excluded-premium-or-subscription-route' };
  }

  if (segments.some(isExcludedFormSegment)) {
    return { eligible: false, reason: 'excluded-form-route' };
  }

  if (ELIGIBLE_PREFIXES.some(prefix => lowerPath === prefix || lowerPath.startsWith(`${prefix}/`))) {
    return { eligible: true, reason: 'eligible-content-route' };
  }

  return { eligible: false, reason: 'unknown-route' };
}

function isExcludedFormSegment(segment: string): boolean {
  return (
    EXCLUDED_FORM_SEGMENTS.has(segment) ||
    segment.startsWith('add-') ||
    segment.startsWith('edit-') ||
    segment.startsWith('create-') ||
    segment.includes('-add-') ||
    segment.includes('-edit-') ||
    segment.includes('-create-') ||
    segment.endsWith('-settings')
  );
}

function isPoolDetailRoute(path: string): boolean {
  return /^\/pool\/[^/]+(?:\/.*)?$/.test(path);
}

function normalizePath(rawUrl: string): string {
  const url = rawUrl || '/';
  const path = url.split('?')[0].split('#')[0] || '/';
  const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
  return withLeadingSlash.length > 1
    ? withLeadingSlash.replace(/\/+$/, '')
    : withLeadingSlash;
}
